import React from 'react';
import { cn } from 'src/lib/utils';
import type { SurveyGroupResultsSurveyPayload } from 'src/types/survey-group-api.types';

export type SurveyGroupSurveyFilterBarProps = {
  surveys: SurveyGroupResultsSurveyPayload[];
  selectedExtSurveyIds: string[];
  onChange: (next: string[]) => void;
};

/**
 * Toggle which surveys from a group are shown in the results body (multi-select).
 */
export const SurveyGroupSurveyFilterBar: React.FC<SurveyGroupSurveyFilterBarProps> = ({
  surveys,
  selectedExtSurveyIds,
  onChange,
}) => {
  const selected = new Set(selectedExtSurveyIds);

  const onPillClick = (extSurveyId: string) => {
    const isOn = selected.has(extSurveyId);
    if (!isOn) {
      onChange([...selectedExtSurveyIds, extSurveyId]);
      return;
    }
    const next = selectedExtSurveyIds.filter((id) => id !== extSurveyId);
    if (next.length === 0) {
      return;
    }
    onChange(next);
  };

  return (
    <div
      className={cn(
        'mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm',
        'ring-1 ring-black/5'
      )}
    >
      <p className="text-sm font-semibold text-gray-900">
        Surveys in this group
      </p>
      <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
        Click a survey to include or exclude it from the combined results below. At least one survey must stay selected.
      </p>
      <div
        className="mt-4 flex flex-wrap gap-2"
        role="group"
        aria-label="Surveys in this group"
      >
        {surveys.map((s) => {
          const id = s.ext_survey_id;
          const isOn = selected.has(id);
          return (
            <button
              key={id}
              type="button"
              aria-pressed={isOn}
              onClick={() => onPillClick(id)}
              className={cn(
                'inline-flex max-w-full items-center rounded-full border px-4 py-2 text-left text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                isOn
                  ? 'border-blue-600 bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                  : 'border-gray-200 bg-gray-50 text-gray-800 hover:border-gray-300 hover:bg-gray-100'
              )}
            >
              <span className="truncate">
                {s.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
